import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { MapPin, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { createLocationRecommendation, applyLocationRecommendation } from '@/lib/apis';
import { useNavigate } from '@tanstack/react-router';
import { campaignsQueryOptions } from '@/lib/query-options';
import { Spinner } from '@/components/ui/spinner';
import { useAtomValue } from 'jotai';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';
import { CountrySelect } from '@/components/glitch-ui/country-select';
import type { Location } from '@/lib/types';

export function LocationOpportunityCard() {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [selectedLocations, setSelectedLocations] = useState<Location[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const organizationData = useAtomValue(organizationDataAtom);

  // Fetch campaigns for the dropdown — canonical options so this card shares
  // the cache entry every other surface reads (#4901; the old inline
  // ['campaigns', orgId] key held a divergent copy that went stale).
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery(
    campaignsQueryOptions(organizationData?.id, googleAdsAccount?.googleads_account_id),
  );

  const createAndApplyMutation = useMutation({
    mutationFn: async () => {
      if (!organizationData?.id || !selectedCampaignId || selectedLocations.length === 0) {
        throw new Error('Organization ID, campaign selection, and location selection required');
      }

      // For now, we'll use the first selected location
      const selectedLocation = selectedLocations[0];

      // Step 1: Create the recommendation with location data
      const recommendation = await createLocationRecommendation(
        organizationData.id.toString(),
        selectedCampaignId,
        {
          location_id: selectedLocation.criteria_id.toString(),
          location_name: selectedLocation.canonical_name
        }
      );

      // Step 2: Apply the recommendation
      const result = await applyLocationRecommendation(recommendation.id);
      
      return result;
    },
    onSuccess: (data) => {
      const locationName = selectedLocations[0]?.canonical_name || 'selected location';
      toast({
        title: 'Success',
        description: `Campaign created for ${locationName}`,
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['recommendations'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      
      // Navigate to the new campaign
      if (data.campaign_id) {
        navigate({
          to: '/campaigns/google/$campaignslug',
          params: { campaignslug: data.campaign_id },
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create campaign',
        variant: 'destructive',
      });
    },
  });

  const handleCreateCampaign = async () => {
    if (!selectedCampaignId) {
      toast({
        title: 'Campaign Required',
        description: 'Please select a campaign to use as a base',
        variant: 'destructive',
      });
      return;
    }

    if (selectedLocations.length === 0) {
      toast({
        title: 'Location Required',
        description: 'Please select at least one location',
        variant: 'destructive',
      });
      return;
    }
    
    setIsCreating(true);
    try {
      await createAndApplyMutation.mutateAsync();
    } finally {
      setIsCreating(false);
    }
  };

  // Get selected campaign name for display
  const selectedCampaign = campaigns.find(c => c.slug === selectedCampaignId);

  // Show loading if organization data is not available
  if (!organizationData) {
    return (
      <Card className="rounded-lg border bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Spinner className="h-4 w-4 mr-2" />
            <span>Loading organization data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-lg border bg-white">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-2">
          <MapPin className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-lg font-semibold text-gray-900">
            Create Location Campaign
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-gray-900">
          <p className="text-sm text-gray-700 mb-2">
            Expand your reach to new markets by creating location-specific campaigns based on your existing campaigns.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="campaign-select" className="text-sm font-medium text-gray-900">
              Select Campaign to Use as Base
            </Label>
            <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
              <SelectTrigger id="campaign-select">
                <SelectValue placeholder={campaignsLoading ? "Loading campaigns..." : campaigns.length === 0 ? "No campaigns available" : "Select a campaign"} />
              </SelectTrigger>
              <SelectContent>
                {campaigns.length === 0 && !campaignsLoading ? (
                  <SelectItem value="no-campaigns" disabled>
                    No campaigns found
                  </SelectItem>
                ) : (
                  campaigns.map((campaign) => (
                    <SelectItem key={campaign.slug} value={campaign.slug}>
                      {campaign.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-900">
              Select Target Location
            </Label>
            <CountrySelect 
              value={selectedLocations} 
              onChange={setSelectedLocations}
              inputPosition="UP"
            />
          </div>
          
          {selectedCampaign && selectedLocations.length > 0 && (
            <div className="text-sm text-gray-700 p-3 bg-gray-50 rounded-md">
              <p>New campaign will be created for <strong>{selectedLocations[0].canonical_name}</strong> using the URL, keywords, and settings from <strong>{selectedCampaign.name}</strong></p>
            </div>
          )}
          
          <Button
            onClick={handleCreateCampaign}
            disabled={isCreating || !selectedCampaignId || campaignsLoading || campaigns.length === 0 || selectedLocations.length === 0}
            className="w-full bg-[#290BD9] hover:bg-[#2309C5] text-white flex items-center justify-center space-x-2"
          >
            {isCreating ? (
              <>
                <Spinner className="h-4 w-4" />
                <span>Creating Campaign...</span>
              </>
            ) : (
              <>
                <span>CREATE CAMPAIGN</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}